/**
 * Per-club sizing of the site chrome.
 *
 * Keyed by subdomain, the way the server keys its other per-club behaviour
 * (SIGN_IN_REQUIRED_SUBDOMAINS and friends in server/routes/tenant.js), so a
 * club opts in by name and no other tenant is touched.
 */

/**
 * Clubs whose header is their own site's nav rather than the platform bar.
 * On uchicago.chessdirector.com the club's hand-built site sits at `/` and
 * the app under `/events`; the two share a header so crossing between them
 * does not read as leaving.
 */
const CLUB_CHROME_SUBDOMAINS = new Set(['uchicago']);

/** The platform's header bar height, in px. */
export const HEADER_HEIGHT = 48;

/**
 * The club nav's height — `.mainnav` in
 * client/public/club-sites/uchicago/css/site.css. Keep the two in step.
 */
export const CLUB_HEADER_HEIGHT = 62;

type TenantLike = { subdomain?: string } | null | undefined;

/** True when this club's header mirrors its own site's nav. */
export function hasClubChrome(tenant: TenantLike): boolean {
  return CLUB_CHROME_SUBDOMAINS.has(String(tenant?.subdomain || '').toLowerCase());
}

/** How tall this club's header bar is, in px. */
export function headerHeightFor(tenant: TenantLike): number {
  return hasClubChrome(tenant) ? CLUB_HEADER_HEIGHT : HEADER_HEIGHT;
}

/**
 * What the header actually takes off the top of the viewport: the bar plus
 * its 1px bottom border. Subtract this, not the bar alone, or a page sized to
 * "the rest of the screen" overflows it by a pixel and gains a scrollbar.
 */
export function headerOffsetFor(tenant: TenantLike): number {
  return headerHeightFor(tenant) + 1;
}
